import { createSlice } from "@reduxjs/toolkit";
import { getDefaultStep } from "../../getDefaultStep";
import { cyclicRangeInclusive, mod } from "../../util/basicAlgebra";
import {
  algorithms,
  hotspotDisplayStates,
  hoverActions,
  steps,
  versions,
} from "../../util/constants";
import { clipToBounds } from "../../util/general";
import { doneWithLocations, dragActiveHotspot, selectHotspot } from "../ui/ui";
import {
  locationModeHotspotClickBehavior,
  pruneKeyframesDefaultTargetCount,
} from "../../config";
import {
  mapValues,
  size,
  pick,
  setWith,
  shuffle,
  take,
  omit,
  isInteger,
  difference,
  max,
} from "lodash";
import {
  getCommittedTheme,
  getCurrentHotspot,
  getCurrentLayer,
  getCurrentFrameIndex,
  getActiveAlgorithm,
  getBounds,
  getFrameCount,
  getKeyFrameCount,
  hotspotVisibleInFrame,
  isWithinBounds,
  getPreviousStep,
  getAlgorithmOutput,
  getAlgorithmState,
  getUserApprovedLocations,
  getVisibleFramesInLayer,
  getHotspotDisplayStateLevel2,
  getAccepted,
  isAlgorithmSwitchPossible,
  getHS,
  getRequestedAlgorithm,
  getNaturalAspect,
} from "./hotspotEditorSelectors";
import { getCircleProjectionVectorized } from "../../algorithms/spin";
import { getVersion } from "../../util/getVersion";

const initialState = {
  currentStep: steps.init,
  previousStep: steps.init,
  filenames: [], // layer/frame
  imageURLs: {}, // filename to url+ map
  hotspots: [], // see newEmptyHotspot() below
  naturalWidth: null,
  naturalHeight: null,
  currentHotspot: null,
  currentFrameIndex: 0,
  currentLayer: 0,
  // Hotspot storage to enable losing changes (on back button click)
  checkpoint: null,
  hotspotIndexOfClickMessageShown: null, //value is hotspot index or null
  hotspotIndexOfHoverMessageShown: null, //value is hotspot index or null
  committedTheme: null,
  candidateBoundaryIndex: "both",
};

export const defaultBox = {
  // default value of 'box' click action
  content: "",
  location: {
    type: "follow",
    fixed: {
      x: {
        keyword: "offset",
        offset: 0,
      },
      y: {
        keyword: "offset",
        offset: 0,
      },
    },
    follow: {
      x: 0,
      y: 0,
    },
  },
};

export const defaultClickAction = {
  box: defaultBox,
  link: "",
};

const defaultHoverAction = {
  tooltip: {
    content: "",
    style: "default",
  },
};

const emptyLocationsHotspot = (state) => {
  const frameCount = getFrameCount(state);
  const version = getVersion();
  const versionToDefaultRequestedAlgorithmMap = {
    [versions.image]: algorithms.manual,
    [versions.spin]: algorithms.spin,
  };
  return {
    bounds: {},
    hidden: {},
    userApprovedLocations: {}, // hotspot locations that the user has
    // visually confirmed on a particular frame to be correct. E.g. for 360
    // spin algo it's not the input keyframe locations, that user never sees,
    // but the output keyframe locations that he sees on the current frame
    // when he releases the drag.
    activeAlgorithm: algorithms.manual,
    algorithmState: {
      [algorithms.manual]: { input: {}, output: {} },
      [algorithms.spin]: { input: {}, output: {}, settings: { frameCount } },
    },
    requestedAlgorithm: versionToDefaultRequestedAlgorithmMap[version],
  };
};

const newEmptyHotspot = (state) => {
  let name = `Hotspot 1`;
  if (state.hotspots.length) {
    // why not just size+1? because user might delete a non-max hotspot and we want still to count up
    let hotspotNameNumberArrayMatchingDefaultPattern = state.hotspots
      .map((x) => /^Hotspot (\d+)$/.exec(x.name)?.[1])
      .filter(Boolean);
    if (hotspotNameNumberArrayMatchingDefaultPattern.length) {
      const nameCounter = max(hotspotNameNumberArrayMatchingDefaultPattern);
      name = `Hotspot ${parseInt(nameCounter) + 1}`;
    }
  }
  return {
    name,
    activeClickAction: "none",
    ...defaultClickAction,
    activeHoverAction: "none",
    ...defaultHoverAction,
    template: null,
    theme: getCommittedTheme(state),
    ...emptyLocationsHotspot(state),
  };
};

function updateManualAlgoFrameReducer(state, { payload }) {
  const { hotspotIndex, layer, frame, mouseCoordinatesInPercent } = payload;
  const algoState = getAlgorithmState(state, {
    hotspotIndex,
    algorithm: algorithms.manual,
  });
  let { x, y } = mouseCoordinatesInPercent;
  x = clipToBounds(x, 0, 100);
  y = clipToBounds(y, 0, 100);
  setWith(algoState.input, [layer, frame], { layer, frame, x, y }, Object);
  //output = input in manual mode
  setWith(algoState.output, [layer, frame], { layer, frame, x, y }, Object);
  hotspotEditorSlice.caseReducers.setHidden(state, {
    payload: {
      hotspotIndex,
      layer,
      frame,
      value: false,
    },
  });
}

function updateManualAlgoAllReducer(state, { payload }) {
  const { hotspotIndex, input } = payload;
  const algoState = getAlgorithmState(state, {
    hotspotIndex,
    algorithm: algorithms.manual,
  });
  algoState.input = input;
  algoState.output = input;
}

function pruneKeyframes(algoState, { payload }) {
  const { layer, bounds, visibleFrames, prune } = payload;
  const targetKeyframeCount = isInteger(prune)
    ? prune
    : pruneKeyframesDefaultTargetCount;
  const keyFrameCount = size(algoState.input[layer]);
  if (
    keyFrameCount <= targetKeyframeCount ||
    visibleFrames.length <= targetKeyframeCount
  ) {
    return;
  }
  // take target keyframe count from the output at random
  const output = algoState.output[layer];
  let aspirants = difference(visibleFrames, bounds);
  aspirants = shuffle(aspirants);
  const survivors = take(aspirants, targetKeyframeCount - 2);
  algoState.input[layer] = pick(output, [...survivors, ...bounds]);
}

function updateSpinAlgoFrameReducer(state, { payload }) {
  const { hotspotIndex, layer, frame, mouseCoordinatesInPercent, prune } =
    payload;
  const algoState = getAlgorithmState(state, {
    hotspotIndex,
    algorithm: algorithms.spin,
  });
  const { x, y } = mouseCoordinatesInPercent;
  // ==== prune keyframes to improve sensitivity of the hotspot path
  const inInput = layer in algoState.input && frame in algoState.input[layer];
  //if incoming frame is not in input -- consider pruning keyframes
  setWith(algoState.input, [layer, frame], { layer, frame, x, y }, Object);
  //for the relevant layer only, others will not change
  updateSpinAlgoLayerReducer(state, {
    payload: { hotspotIndex, layer, prune: prune || !inInput },
  });
}

function updateSpinAlgoLayerReducer(state, { payload }) {
  const { hotspotIndex, layer, prune = false } = payload;
  const algoState = getAlgorithmState(state, {
    hotspotIndex,
    algorithm: algorithms.spin,
  });
  const imageXYPercentObject = algoState.input[layer];
  const frameCount = getFrameCount(state);
  const aspect = getNaturalAspect(state);
  let { output, clipping } = getCircleProjectionVectorized({
    imageXYPercentObject,
    frameCount,
    aspect,
  });
  // remove clipping hotspots from the output
  output = omit(output, clipping);
  output = mapValues(output, (frameObject) => ({ layer, ...frameObject }));
  setWith(algoState.output, [layer], output, Object);
  if (prune) {
    // `prune  can be integer to force a non-default keyframe count
    const bounds = getBounds(state, { payload });
    const visibleFrames = getVisibleFramesInLayer(state, {
      hotspotIndex,
      layer,
    });
    pruneKeyframes(algoState, {
      payload: { layer, visibleFrames, bounds, prune },
    });
  }
  // show notification if path is hyperbolic
  if (clipping.length) {
    hotspotEditorSlice.caseReducers.setNotification(state, {
      payload: {
        message: `Can't fit a good path to provided points`,
        duration: 4000,
      },
    });
  } else {
    hotspotEditorSlice.caseReducers.setNotification(state, { payload: null });
  }
}

function updateSpinAlgoAllReducer(state, { payload }) {
  const { hotspotIndex, input, prune = false } = payload;
  // overwrite input
  const algoState = getAlgorithmState(state, {
    hotspotIndex,
    algorithm: algorithms.spin,
  });
  algoState.input = input;
  for (let layer of Object.keys(input)) {
    updateSpinAlgoLayerReducer(state, {
      payload: { layer, hotspotIndex, prune },
    });
  }
}

function updateAlgorithmInputReducer(state, action) {
  const {
    hotspotIndex = getCurrentHotspot(state),
    layer = getCurrentLayer(state),
    frame = getCurrentFrameIndex(state),
    mouseCoordinatesInPercent, //calling it mouse on purpose to exclude confusion with `pointer` field in spin JSON
    prune = false,
  } = action.payload;
  const hs = state.hotspots[hotspotIndex];
  const algorithm = getActiveAlgorithm(state, { hotspotIndex });
  const payload = {
    hotspotIndex,
    layer,
    frame,
    mouseCoordinatesInPercent,
    prune,
  };

  switch (algorithm) {
    case algorithms.manual:
      updateManualAlgoFrameReducer(state, { payload });
      break;
    case algorithms.spin:
      updateSpinAlgoFrameReducer(state, { payload });
      break;
    default:
      throw new Error(`unknown algorithm ${algorithm}`);
  }
  const algoState = getAlgorithmState(state, { hotspotIndex, algorithm });
  setWith(
    hs.userApprovedLocations,
    [layer, frame],
    algoState.output[layer][frame],
    Object
  );
}

const updateCandidateBoundaryReducer = (
  state,
  {
    payload: {
      previousFrameIndex,
      currentFrameIndex = getCurrentFrameIndex(state),
      direction,
      resetTracking = false,
    },
  }
) => {
  const bounds = getBounds(state) ?? []; // defaults to [] when no hotspot is there for click to place first hotspot behavior
  if (bounds.length === 0) {
    state.candidateBoundaryIndex = "both";
    return;
  }
  if (bounds.length === 1) {
    throw new Error("Impossible situation, only a single boundary is set");
  }

  const N = getFrameCount(state);
  const cyclicRangeN = (a, b) =>
    cyclicRangeInclusive({
      boundaries: [a, b],
      N,
    });
  //if current frame within bounds -- bounds will not change
  if (cyclicRangeN(...bounds).includes(currentFrameIndex)) {
    state.candidateBoundaryIndex = null;
    return;
  }

  //if tracking reset requested (switching hotspot for example),
  // previousFrameIndex is irrelevant in this case, we use the closest boundary
  if (resetTracking) {
    state.candidateBoundaryIndex =
      cyclicRangeN(bounds[1], currentFrameIndex) >
      cyclicRangeN(currentFrameIndex, bounds[0])
        ? 0
        : 1;
  } else {
    //if crossing one of the bounds
    const prev2curr = cyclicRangeN(previousFrameIndex, currentFrameIndex);
    const curr2prev = cyclicRangeN(currentFrameIndex, previousFrameIndex);
    if (prev2curr.includes(bounds[1]) && direction > 0) {
      state.candidateBoundaryIndex = 1;
    } else if (curr2prev.includes(bounds[0]) && direction < 0) {
      state.candidateBoundaryIndex = 0;
    }
    return;
  }
};

const commitBoundaryReducer = (state) => {
  const frame = getCurrentFrameIndex(state);
  const candidate = state.candidateBoundaryIndex;
  switch (candidate) {
    case null:
      break;
    case 0:
    case 1:
      hotspotEditorSlice.caseReducers.setBoundary(state, {
        payload: {
          index: candidate,
          value: frame,
        },
      });
      break;
    case "both":
      hotspotEditorSlice.caseReducers.setBoundaries(state, {
        payload: { boundaries: [frame, frame] },
      });
      break;
    default:
      throw new Error(`unknown candidate boundary value ${candidate}`);
  }
};

const autoEnableSpinAlgorithmReducer = (state) => {
  const requestedAlgorithm = getRequestedAlgorithm(state);
  if (requestedAlgorithm === algorithms.spin) {
    const keyFrameCount = getKeyFrameCount(state);
    if (keyFrameCount === 3) {
      hotspotEditorSlice.caseReducers.setActiveAlgorithm(state, {
        payload: { activeAlgorithm: algorithms.spin },
      });
    }
  }
};

export const hotspotEditorSlice = createSlice({
  name: "editor",
  initialState,
  reducers: {
    setNaturalDimensions(state, { payload }) {
      const { naturalWidth, naturalHeight } = payload;
      state.naturalHeight = naturalHeight;
      state.naturalWidth = naturalWidth;
    },
    setFilenamesInLayer(state, { payload }) {
      const { layer = getCurrentLayer(state), filenames } = payload;
      state.filenames[layer] = filenames;
    },
    setRequestedImageURL(state, { payload }) {
      const { filename, key, url } = payload;
      setWith(
        state,
        ["imageURLs", filename, "urlsRequested", key],
        { url },
        Object
      );
    },
    setAvailableImageURL(state, { payload }) {
      const { filename, key, url } = payload;
      setWith(
        state,
        ["imageURLs", filename, "urlsAvailable", key],
        { url },
        Object
      );
    },
    setRequestedAlgorithm(state, { payload }) {
      const { algorithm, hotspotIndex = getCurrentHotspot(state) } = payload;
      const { setActiveAlgorithm } = hotspotEditorSlice.caseReducers;
      const hs = getHS(state, { hotspotIndex });
      hs.requestedAlgorithm = algorithm;
      setActiveAlgorithm(state, {
        payload: { hotspotIndex, activeAlgorithm: algorithm },
      });
    },
    locationChangeIntent(state, action) {
      updateAlgorithmInputReducer(state, action);
      commitBoundaryReducer(state, action);
    },

    locationSetIntent(state, action) {
      let effectivePayload = action.payload;
      // if hotspot is visible in this frame, do nothing
      const display = getHotspotDisplayStateLevel2(state);
      if (
        [
          hotspotDisplayStates.normal,
          hotspotDisplayStates.approved,
          hotspotDisplayStates.accepted,
        ].includes(display)
      ) {
        return;
      }
      //if hotspot is explicitly hidden unhide it {
      if (
        [
          hotspotDisplayStates.hiddenAccepted,
          hotspotDisplayStates.hiddenApproved,
        ].includes(display)
      ) {
        hotspotEditorSlice.caseReducers.setHidden(state, {
          payload: { value: false },
        });
        // if algorithm is spin, then reduce the keyframe count to ensure that
        // the new hotspot lands under mouse cursor on click
        const activeAlgorithm = getActiveAlgorithm(state);
        if (activeAlgorithm === algorithms.spin) {
          const prune = 3;
          effectivePayload = { ...effectivePayload, prune };
        }
      }
      //else set location with existing reducer
      hotspotEditorSlice.caseReducers.locationChangeIntent(state, {
        payload: effectivePayload,
      });
      autoEnableSpinAlgorithmReducer(state);
    },
    resetHotspotEditor(state) {
      return { ...initialState, currentStep: getDefaultStep() };
    },
    setCurrentStep(state, action) {
      const step = action.payload;
      if (
        getVersion() === versions.image &&
        step === steps.configureLocations
      ) {
        return;
      }
      const prev = "" + state.currentStep; // prevents bugs due to state being an immer.js proxy
      if (prev !== step) {
        state.previousStep = prev;
      }

      if (step === steps.preview) {
        hotspotEditorSlice.caseReducers.setHotspotIndexOfHoverMessageShown(
          state,
          { payload: null }
        );
      }
      if (step === steps.configureHoverAction) {
        hotspotEditorSlice.caseReducers.setHotspotIndexOfHoverMessageShown(
          state,
          { payload: state.currentHotspot }
        );
      }
      if ([steps.pickTheme, steps.advanced].includes(step)) {
        hotspotEditorSlice.caseReducers.setCurrentHotspot(state, {
          payload: null,
        });
      }
      if (
        steps.configureLocations === step &&
        prev !== steps.configureLocations
      ) {
        hotspotEditorSlice.caseReducers.checkpointHotspot(state);
      }
      updateCandidateBoundaryReducer(state, {
        payload: { resetTracking: true },
      });
      state.currentStep = step;
    },
    returnToPreviousStep(state) {
      const prev = getPreviousStep(state);
      hotspotEditorSlice.caseReducers.setCurrentStep(state, { payload: prev });
    },
    setDimensions(state, action) {
      const { width, height } = action.payload;
      state.width = width;
      state.height = height;
    },
    setCurrentFrameIndex(state, action) {
      let { frameIndex } = action.payload;
      const previousFrameIndex = getCurrentFrameIndex(state);
      state.previousFrameIndex = previousFrameIndex;
      const frameCount = getFrameCount(state);
      const currentFrameIndex = mod(frameIndex, frameCount);
      if (currentFrameIndex === previousFrameIndex) {
        return;
      }
      const direction =
        mod(currentFrameIndex - previousFrameIndex, frameCount) < frameCount / 2
          ? 1
          : -1;

      updateCandidateBoundaryReducer(state, {
        payload: {
          previousFrameIndex,
          currentFrameIndex,
          direction,
        },
      });
      state.currentFrameIndex = currentFrameIndex;
    },

    goNextFrame(state) {
      const frame = getCurrentFrameIndex(state) + 1;
      hotspotEditorSlice.caseReducers.setCurrentFrameIndex(state, {
        payload: { frameIndex: frame, direction: +1 },
      });
    },
    goPreviousFrame(state) {
      const frame = getCurrentFrameIndex(state) - 1;
      hotspotEditorSlice.caseReducers.setCurrentFrameIndex(state, {
        payload: { frameIndex: frame, direction: -1 },
      });
    },

    setCurrentHotspot(state, action) {
      const hotspotIndex = action.payload;
      state.currentHotspot = hotspotIndex;
      state.previousStep = state.currentStep;
      if (hotspotIndex === null) {
        hotspotEditorSlice.caseReducers.setCurrentStep(state, {
          payload: steps.preview,
        });
        hotspotEditorSlice.caseReducers.setHotspotIndexOfHoverMessageShown(
          state,
          {
            payload: null,
          }
        );
        hotspotEditorSlice.caseReducers.setHotspotIndexOfClickMessageShown(
          state,
          {
            payload: null,
          }
        );
        return;
      }
      const clickAction = state.hotspots[hotspotIndex]?.activeClickAction;
      if (clickAction && clickAction !== "none") {
        hotspotEditorSlice.caseReducers.setCurrentStep(state, {
          payload: steps.preview,
        });
        return;
      }
      const hoverAction = state.hotspots[hotspotIndex]?.activeHoverAction;
      if (hoverAction && hoverAction !== "none") {
        hotspotEditorSlice.caseReducers.setCurrentStep(state, {
          payload: steps.preview,
        });
        return;
      }
      if (getKeyFrameCount(state) > 0) {
        hotspotEditorSlice.caseReducers.setCurrentStep(state, {
          payload: steps.preview,
        });
        return;
      }
      hotspotEditorSlice.caseReducers.setCurrentStep(state, {
        payload: steps.configureLocations,
      });
    },
    createHotspot(state, action) {
      const hotspot = { ...newEmptyHotspot(state), ...action.payload };
      state.hotspots.push(hotspot);
      state.candidateBoundaryIndex = "both";
    },
    deleteHotspot(state, action) {
      const hotspotIndex = action.payload ?? getCurrentHotspot(state);
      if (hotspotIndex === null) {
        return;
      }
      state.hotspots.splice(hotspotIndex, 1);
      hotspotEditorSlice.caseReducers.setCurrentHotspot(state, {
        payload: null,
      });
      hotspotEditorSlice.caseReducers.setCurrentStep(state, {
        payload: steps.preview,
      });
    },

    // Hotspot visibility state
    setHidden(state, action) {
      const {
        hotspotIndex = getCurrentHotspot(state),
        frame = getCurrentFrameIndex(state),
        layer = getCurrentLayer(state),
        value = true,
      } = action.payload ?? {};
      const hidden = state.hotspots[hotspotIndex].hidden;
      if (!hidden[layer]) {
        hidden[layer] = [];
      }
      hidden[layer][frame] = value;
    },
    toggleHidden(state, action) {
      const {
        hotspotIndex = getCurrentHotspot(state),
        frame = getCurrentFrameIndex(state),
        layer = getCurrentLayer(state),
      } = action.payload ?? {};
      const hidden = state.hotspots[hotspotIndex].hidden;
      if (!hidden[layer]) {
        hidden[layer] = [];
      }
      const accepted = getAccepted(state, { hotspotIndex, layer, frame });
      if (!accepted) {
        return;
      }
      const effectiveVisibility = hotspotVisibleInFrame(state, {
        frame,
        hotspotIndex,
      });
      hidden[layer][frame] = effectiveVisibility;
    },
    setBoundary(state, action) {
      //depends on number of frames from modulo, so must be in this slice
      const {
        hotspotIndex = state.currentHotspot,
        layer = getCurrentLayer(state),
        index,
        value,
      } = action.payload;
      const N = getFrameCount(state);
      if (!(index in [0, 1])) {
        throw new Error(
          "Boundary index should be either 0 or 1, i.e. first or last frame"
        );
      }
      state.hotspots[hotspotIndex].bounds[layer][index] = mod(value, N);
    },
    setBoundaries(state, action) {
      let {
        layer = getCurrentLayer(state),
        boundaries,
        hotspotIndex = getCurrentHotspot(state),
      } = action.payload;
      const N = getFrameCount(state);
      boundaries = boundaries.map((value) => mod(value, N));
      state.hotspots[hotspotIndex].bounds[layer] = boundaries;
    },
    // setNearestBoundary(state, action) {
    //   const {
    //     frame = getCurrentFrameIndex(state),
    //     hotspotIndex = getCurrentHotspot(state),
    //     N = getFrameCount(state),
    //   } = action.payload ?? {};
    //   const boundaries = getBounds(state, { hotspotIndex });
    //   const d0 = shortestCyclicDistance(frame, boundaries[0], N);
    //   const d1 = shortestCyclicDistance(frame, boundaries[1], N);
    //   if (d1 >= d0) {
    //     hotspotEditorSlice.caseReducers.setBoundary(state, {
    //       payload: {
    //         hotspotIndex,
    //         index: 0,
    //         value: frame,
    //       },
    //     });
    //   } else {
    //     hotspotEditorSlice.caseReducers.setBoundary(state, {
    //       payload: {
    //         hotspotIndex,
    //         index: 1,
    //         value: frame,
    //       },
    //     });
    //   }
    // },
    clickLocationModeHotspot(state, action) {
      const {
        hotspotIndex = getCurrentHotspot(state),
        frame = getCurrentFrameIndex(state),
        layer = getCurrentLayer(state),
      } = action.payload ?? {};

      const effectiveAction = { payload: { hotspotIndex, frame, layer } };
      if (locationModeHotspotClickBehavior === "hybrid") {
        const withinBounds = isWithinBounds(state, {
          frame,
          hotspotIndex,
        });
        if (withinBounds) {
          hotspotEditorSlice.caseReducers.toggleHidden(state, effectiveAction);
        } else {
          hotspotEditorSlice.caseReducers.setNearestBoundary(
            state,
            effectiveAction
          );
        }
      } else {
        hotspotEditorSlice.caseReducers.toggleHidden(state, effectiveAction);
      }
    },

    updateAlgorithmInput: updateAlgorithmInputReducer,
    cancelPlacement: (state) => {
      const prev = getPreviousStep(state);
      hotspotEditorSlice.caseReducers.setCurrentStep(state, { payload: prev });
      hotspotEditorSlice.caseReducers.restoreCheckpoint(state);
    },

    setHoverActionType: (state, action) => {
      const { hotspotIndex, actionType } = action.payload;
      state.hotspots[hotspotIndex].activeHoverAction = actionType;
    },
    setTooltipContent: (state, action) => {
      const { hotspotIndex, value } = action.payload;
      state.hotspots[hotspotIndex].tooltip.content = value;
      hotspotEditorSlice.caseReducers.setHoverActionType(state, {
        payload: {
          hotspotIndex,
          actionType: value !== "" ? hoverActions.tooltip : hoverActions.none,
        },
      });
    },
    setHoverAction: (state, action) => {
      const { hotspotIndex, actionType, actionValue } = action.payload;
      if (!Object.keys(hoverActions).includes(actionType)) {
        throw new Error(
          `${actionType} is not a valid hover action type for hotspot`
        );
      }
      if (actionType !== "none") {
        state.hotspots[hotspotIndex][actionType] = actionValue;
      }
    },
    setTooltipStyle: (state, action) => {
      const { hotspotIndex, value } = action.payload;
      state.hotspots[hotspotIndex].tooltip.style = value;
    },
    setClickActionType: (state, action) => {
      const { hotspotIndex, actionType } = action.payload;
      state.hotspots[hotspotIndex].activeClickAction = actionType;
    },
    setClickActionBoxStyle: (state, action) => {
      const { hotspotIndex, style } = action.payload;
      state.hotspots[hotspotIndex].box.style = style;
    },
    setClickActionBoxLocationType: (state, action) => {
      const { hotspotIndex, value } = action.payload;
      state.hotspots[hotspotIndex].box.location.type = value;
    },
    setClickActionBoxContent: (state, action) => {
      const { hotspotIndex, content } = action.payload;
      state.hotspots[hotspotIndex].box.content = content;
    },
    setClickActionBoxLocationFollowOffset: (state, action) => {
      const { hotspotIndex, coordinateName, coordinateValue } = action.payload;
      state.hotspots[hotspotIndex].box.location.follow[coordinateName] =
        coordinateValue;
    },
    setClickActionLocationFixedKeyword: (state, action) => {
      const { hotspotIndex, coordinateName, keywordValue } = action.payload;
      state.hotspots[hotspotIndex].box.location.fixed[coordinateName].keyword =
        keywordValue;
    },
    setClickActionLocationFixedOffset: (state, action) => {
      const { hotspotIndex, coordinateName, offsetValue } = action.payload;
      state.hotspots[hotspotIndex].box.location.fixed[coordinateName].offset =
        offsetValue;
    },
    setClickActionLink: (state, action) => {
      const { hotspotIndex, actionValue } = action.payload;
      state.hotspots[hotspotIndex].link = actionValue;
    },
    checkpointHotspot: (state) => {
      const hotspotIndex = getCurrentHotspot(state);
      state.checkpoint = state.hotspots[hotspotIndex];
    },
    restoreCheckpoint: (state) => {
      const hotspotIndex = getCurrentHotspot(state);
      if (state.checkpoint) {
        state.hotspots[hotspotIndex] = state.checkpoint;
      } else {
        hotspotEditorSlice.caseReducers.deleteHotspot(state, {
          payload: hotspotIndex,
        });
      }
    },
    clearCheckpoint: (state) => {
      state.checkpoint = null;
    },
    clearLocations: (state, action) => {
      const { hotspotIndex = getCurrentHotspot(state) } = action?.payload ?? {};
      state.hotspots[hotspotIndex] = {
        ...state.hotspots[hotspotIndex],
        ...emptyLocationsHotspot(state),
      };
      state.candidateBoundaryIndex = "both";
    },
    setHotspotIndexOfClickMessageShown: (state, action) => {
      state.hotspotIndexOfClickMessageShown = action.payload;
    },
    setHotspotIndexOfHoverMessageShown: (state, action) => {
      state.hotspotIndexOfHoverMessageShown = action.payload;
    },
    setHotspotName: (state, action) => {
      const { hotspotIndex, name } = action.payload;
      state.hotspots[hotspotIndex].name = name;
    },
    setActiveAlgorithm: (state, action) => {
      const {
        hotspotIndex = getCurrentHotspot(state),
        activeAlgorithm: newAlgorithm,
      } = action.payload;
      const oldAlgorithm = state.hotspots[hotspotIndex].activeAlgorithm;
      if (oldAlgorithm === newAlgorithm) {
        return;
      }
      if (!isAlgorithmSwitchPossible(state, { to: newAlgorithm })) {
        return;
      }
      if (oldAlgorithm === algorithms.manual) {
        if (newAlgorithm === algorithms.spin) {
          const input = getUserApprovedLocations(state, {
            payload: { hotspotIndex },
          });
          updateSpinAlgoAllReducer(state, {
            payload: { hotspotIndex, input, prune: true },
          });

          // make manual points keyframes
        }
      } else if (oldAlgorithm === algorithms.spin) {
        if (newAlgorithm === algorithms.manual) {
          const oldAlgoOutput = getAlgorithmOutput(state, {
            hotspotIndex,
            algorithm: oldAlgorithm,
          });
          const layers = Object.keys(oldAlgoOutput);
          const input = {};
          for (const layer of layers) {
            const frames = getVisibleFramesInLayer(state, {
              hotspotIndex,
              layer,
            });
            for (const frame of frames) {
              setWith(
                input,
                [layer, frame],
                oldAlgoOutput[layer][frame],
                Object
              );
            }
          }
          updateManualAlgoAllReducer(state, {
            payload: { hotspotIndex, input },
          });

          // make manual points keyframes
        }
      }
      state.hotspots[hotspotIndex].activeAlgorithm = newAlgorithm;
    },
    setAlgorithmInput: (state, action) => {
      const { hotspotIndex, algorithmInput } = action.payload;
      state.hotspots[hotspotIndex].algorithmInput = algorithmInput;
    },
    setTemplate: (state, action) => {
      const { hotspotIndex, template } = action.payload;
      state.hotspots[hotspotIndex].template = template;
    },
    setPointerText: (state, action) => {
      const { hotspotIndex, text } = action.payload;
      state.hotspots[hotspotIndex].pointerText = text;
    },
    setNotification(state, action) {
      state.notification = action.payload;
    },
    setCommittedTheme(state, action) {
      state.committedTheme = action.payload;
      hotspotEditorSlice.caseReducers.setCurrentStep(state, {
        payload: steps.preview,
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(dragActiveHotspot().type, (state, action) => {
      const { setCurrentStep, updateAlgorithmInput } =
        hotspotEditorSlice.caseReducers;
      setCurrentStep(state, { payload: steps.configureLocations });
      updateAlgorithmInput(state, action);
    });
    builder.addCase(doneWithLocations().type, (state) =>
      hotspotEditorSlice.caseReducers.setCurrentStep(state, {
        payload: steps.preview,
      })
    );
    builder.addCase(selectHotspot().type, (state, action) => {
      const { hotspotIndex } = action.payload;
      const { setCurrentHotspot, setHotspotIndexOfHoverMessageShown } =
        hotspotEditorSlice.caseReducers;
      setCurrentHotspot(state, {
        payload: hotspotIndex,
      });
      setHotspotIndexOfHoverMessageShown(state, { payload: null });
    });
  },
});

export const {
  setNaturalDimensions,
  setFilenamesInLayer,
  setRequestedImageURL,
  setAvailableImageURL,
  setRequestedAlgorithm,
  resetHotspotEditor,
  setCurrentStep,
  returnToPreviousStep,
  setDimensions,
  setCurrentFrameIndex,
  goNextFrame,
  goPreviousFrame,
  setCurrentHotspot,
  createHotspot,
  deleteHotspot,
  setHidden,
  toggleHidden,
  setBoundary,
  setBoundaries,
  // setNearestBoundary,
  clickLocationModeHotspot,
  updateAlgorithmInput,
  cancelPlacement,
  setHoverActionType,
  setTooltipContent,
  setHoverAction,
  setTooltipStyle,
  // click action
  setClickActionType,
  setClickActionBoxStyle,
  setClickActionBoxContent,
  setClickActionLink,
  setClickActionBoxLocationFollowOffset,
  setClickActionBoxLocationType,
  setClickActionLocationFixedKeyword,
  setClickActionLocationFixedOffset,
  setHotspotIndexOfClickMessageShown,
  setHotspotIndexOfHoverMessageShown,
  // checkpointing (back button handling)
  checkpointHotspot,
  restoreCheckpoint,
  clearCheckpoint,
  clearLocations,
  setHotspotName,
  setActiveAlgorithm,
  setAlgorithmInput,
  // ui stuff, TODO: consider moving into another reducer
  setTemplate,
  setPointerText,
  setNotification,
  setCommittedTheme,
  locationChangeIntent,
  locationSetIntent,
} = hotspotEditorSlice.actions;

export default hotspotEditorSlice.reducer;
